/**
 * Whitelisted profile social links — handles only, no freeform URLs in bio.
 */

export const SOCIAL_PLATFORM_ORDER = [
  'instagram',
  'tiktok',
  'x',
  'snapchat',
  'youtube',
  'twitch',
  'facebook',
  'threads',
  'pinterest',
  'reddit',
  'onlyfans',
  'fansly',
  'patreon',
] as const

export type SocialPlatform = (typeof SOCIAL_PLATFORM_ORDER)[number]

export type SocialLink = {
  platform: SocialPlatform
  handle: string
  url: string
  label: string
}

export const SOCIAL_PLATFORM_META: Record<
  SocialPlatform,
  { label: string; placeholder: string; buildUrl: (handle: string) => string }
> = {
  instagram: {
    label: 'Instagram',
    placeholder: 'username',
    buildUrl: (h) => `https://instagram.com/${h}`,
  },
  tiktok: {
    label: 'TikTok',
    placeholder: 'username',
    buildUrl: (h) => `https://tiktok.com/@${h}`,
  },
  x: {
    label: 'X',
    placeholder: 'username',
    buildUrl: (h) => `https://x.com/${h}`,
  },
  snapchat: {
    label: 'Snapchat',
    placeholder: 'username',
    buildUrl: (h) => `https://snapchat.com/add/${h}`,
  },
  youtube: {
    label: 'YouTube',
    placeholder: '@channel or handle',
    buildUrl: (h) => (h.startsWith('@') ? `https://youtube.com/${h}` : `https://youtube.com/@${h}`),
  },
  twitch: {
    label: 'Twitch',
    placeholder: 'username',
    buildUrl: (h) => `https://twitch.tv/${h}`,
  },
  facebook: {
    label: 'Facebook',
    placeholder: 'username or page',
    buildUrl: (h) => `https://facebook.com/${h}`,
  },
  threads: {
    label: 'Threads',
    placeholder: 'username',
    buildUrl: (h) => `https://threads.net/@${h}`,
  },
  pinterest: {
    label: 'Pinterest',
    placeholder: 'username',
    buildUrl: (h) => `https://pinterest.com/${h}`,
  },
  reddit: {
    label: 'Reddit',
    placeholder: 'username',
    buildUrl: (h) => `https://reddit.com/user/${h}`,
  },
  onlyfans: {
    label: 'OnlyFans',
    placeholder: 'username',
    buildUrl: (h) => `https://onlyfans.com/${h}`,
  },
  fansly: {
    label: 'Fansly',
    placeholder: 'username',
    buildUrl: (h) => `https://fansly.com/${h}`,
  },
  patreon: {
    label: 'Patreon',
    placeholder: 'username',
    buildUrl: (h) => `https://patreon.com/${h}`,
  },
}

const HANDLE_RULES: Record<SocialPlatform, RegExp> = {
  instagram: /^[a-zA-Z0-9._]{1,30}$/,
  tiktok: /^[a-zA-Z0-9._]{2,24}$/,
  x: /^[a-zA-Z0-9_]{1,15}$/,
  snapchat: /^[a-zA-Z0-9._-]{3,15}$/,
  youtube: /^@?[a-zA-Z0-9._-]{3,30}$/,
  twitch: /^[a-zA-Z0-9_]{4,25}$/,
  facebook: /^[a-zA-Z0-9.]{3,50}$/,
  threads: /^[a-zA-Z0-9._]{1,30}$/,
  pinterest: /^[a-zA-Z0-9._]{3,30}$/,
  reddit: /^[a-zA-Z0-9_-]{3,20}$/,
  onlyfans: /^[a-zA-Z0-9._-]{3,30}$/,
  fansly: /^[a-zA-Z0-9._-]{3,30}$/,
  patreon: /^[a-zA-Z0-9_-]{3,30}$/,
}

const URL_EXTRACTORS: Record<SocialPlatform, RegExp[]> = {
  instagram: [/instagram\.com\/([a-zA-Z0-9._]+)/i],
  tiktok: [/tiktok\.com\/@?([a-zA-Z0-9._]+)/i],
  x: [/(?:x|twitter)\.com\/([a-zA-Z0-9_]+)/i],
  snapchat: [/snapchat\.com\/add\/([a-zA-Z0-9._-]+)/i],
  youtube: [/youtube\.com\/(@?[a-zA-Z0-9._-]+)/i],
  twitch: [/twitch\.tv\/([a-zA-Z0-9_]+)/i],
  facebook: [/facebook\.com\/([a-zA-Z0-9.]+)/i],
  threads: [/threads\.net\/@?([a-zA-Z0-9._]+)/i],
  pinterest: [/pinterest\.com\/([a-zA-Z0-9._]+)/i],
  reddit: [/reddit\.com\/u(?:ser)?\/([a-zA-Z0-9_-]+)/i],
  onlyfans: [/onlyfans\.com\/([a-zA-Z0-9._-]+)/i],
  fansly: [/fansly\.com\/([a-zA-Z0-9._-]+)/i],
  patreon: [/patreon\.com\/([a-zA-Z0-9_-]+)/i],
}

export const MAX_SOCIAL_LINKS = 8

export function isSocialPlatform(value: string): value is SocialPlatform {
  return (SOCIAL_PLATFORM_ORDER as readonly string[]).includes(value)
}

export function normalizeSocialHandle(platform: SocialPlatform, raw: string): string {
  let s = raw.trim()
  if (!s) return ''
  if (/^https?:\/\//i.test(s) || s.includes('/')) {
    for (const re of URL_EXTRACTORS[platform]) {
      const m = s.match(re)
      if (m?.[1]) s = m[1]
    }
  }
  s = s.replace(/^@/, '').replace(/^u\//i, '').trim()
  if (platform === 'youtube' && !s.startsWith('@') && /^[a-zA-Z]/.test(s)) {
    // keep plain handles; buildUrl adds @ when needed
  }
  return s
}

export function validateSocialHandle(platform: SocialPlatform, raw: string): string | null {
  const handle = normalizeSocialHandle(platform, raw)
  if (!handle) return null
  if (/https?:|www\.|\//i.test(handle)) return 'Use your username only — not a full link.'
  if (/\d{3}[-.\s]?\d{3}[-.\s]?\d{4}/.test(handle)) return 'Phone numbers are not allowed in social links.'
  if (!HANDLE_RULES[platform].test(handle)) {
    return `Invalid ${SOCIAL_PLATFORM_META[platform].label} username.`
  }
  return null
}

export function buildSocialLink(platform: SocialPlatform, raw: string): SocialLink | null {
  const handle = normalizeSocialHandle(platform, raw)
  if (!handle) return null
  const err = validateSocialHandle(platform, handle)
  if (err) return null
  const meta = SOCIAL_PLATFORM_META[platform]
  return {
    platform,
    handle,
    url: meta.buildUrl(handle),
    label: meta.label,
  }
}

export type SocialLinksInput = Partial<Record<SocialPlatform, string | null | undefined>>

export function parseSocialLinksInput(input: SocialLinksInput): { links: SocialLink[]; errors: string[] } {
  const errors: string[] = []
  const links: SocialLink[] = []
  for (const platform of SOCIAL_PLATFORM_ORDER) {
    const raw = input[platform]
    if (raw == null || String(raw).trim() === '') continue
    const err = validateSocialHandle(platform, String(raw))
    if (err) {
      errors.push(`${SOCIAL_PLATFORM_META[platform].label}: ${err}`)
      continue
    }
    const link = buildSocialLink(platform, String(raw))
    if (link) links.push(link)
  }
  if (links.length > MAX_SOCIAL_LINKS) {
    return { links: links.slice(0, MAX_SOCIAL_LINKS), errors: [`Maximum ${MAX_SOCIAL_LINKS} social links.`] }
  }
  return { links, errors }
}

/** Bio/heading must not contain URLs, domains, or phone numbers — use Social Links instead. */
export function rejectFreeformContactInProfileText(text: string | null | undefined): string | null {
  if (!text?.trim()) return null
  const t = text.trim()
  if (/(?:https?:\/\/|www\.)\S+/i.test(t)) {
    return 'Links belong in Social Links — remove URLs from your bio or heading.'
  }
  if (/\b[a-z0-9-]+\.(com|net|org|io|co|me|tv|gg|ly|app|link)\b/i.test(t)) {
    return 'Website addresses belong in Social Links — use the dedicated fields instead.'
  }
  if (/(?:\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/.test(t)) {
    return 'Phone numbers are not allowed in bio or heading.'
  }
  if (/\b(?:snap|insta|ig|dm me on|text me|whatsapp|telegram)\b[^.!?]{0,40}\d/i.test(t)) {
    return 'Move contact info to verified Social Links instead of your bio.'
  }
  return null
}
